// src/admin/SuperAdminDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Users,
  FileText,
  Shield,
  Activity,
  DollarSign,
  BarChart,
} from 'lucide-react';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalBusinesses: 0,
    activeBusinesses: 0,
    totalPortals: 0,
    revenueMonth: 0,
    activePlans: {
      starter: 0,
      professional: 0,
      business: 0,
      enterprise: 0,
    },
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would be an API call
        // const response = await fetch('/.netlify/functions/get-admin-dashboard');
        // const data = await response.json();
        // setStats(data);

        // For demo purposes, we'll use mock data
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API delay

        setStats({
          totalBusinesses: 78,
          activeBusinesses: 65,
          totalPortals: 203,
          revenueMonth: 7849,
          activePlans: {
            starter: 42,
            professional: 28,
            business: 8,
            enterprise: 0,
          },
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const planData = [
    {
      id: 'starter',
      name: 'Starter',
      count: stats.activePlans.starter,
      price: 29,
      percentTotal:
        Math.round((stats.activePlans.starter / stats.totalBusinesses) * 100) ||
        0,
      revenue: stats.activePlans.starter * 29,
    },
    {
      id: 'professional',
      name: 'Professional',
      count: stats.activePlans.professional,
      price: 79,
      percentTotal:
        Math.round(
          (stats.activePlans.professional / stats.totalBusinesses) * 100
        ) || 0,
      revenue: stats.activePlans.professional * 79,
    },
    {
      id: 'business',
      name: 'Business',
      count: stats.activePlans.business,
      price: 199,
      percentTotal:
        Math.round(
          (stats.activePlans.business / stats.totalBusinesses) * 100
        ) || 0,
      revenue: stats.activePlans.business * 199,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      count: stats.activePlans.enterprise,
      price: 1997,
      percentTotal:
        Math.round(
          (stats.activePlans.enterprise / stats.totalBusinesses) * 100
        ) || 0,
      revenue: stats.activePlans.enterprise * 1997,
    },
  ];

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Platform Dashboard
        </h1>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <>
            {/* Key Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-indigo-100">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Total Businesses
                    </h3>
                    <p className="text-3xl font-bold text-indigo-600">
                      {stats.totalBusinesses}
                    </p>
                    <p className="text-sm text-gray-500">
                      {stats.activeBusinesses} active (
                      {Math.round(
                        (stats.activeBusinesses / stats.totalBusinesses) * 100
                      )}
                      %)
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Client Portals
                    </h3>
                    <p className="text-3xl font-bold text-blue-600">
                      {stats.totalPortals}
                    </p>
                    <p className="text-sm text-gray-500">
                      Avg{' '}
                      {(stats.totalPortals / stats.activeBusinesses).toFixed(1)}{' '}
                      per business
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Monthly Revenue
                    </h3>
                    <p className="text-3xl font-bold text-green-600">
                      ${stats.revenueMonth.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      $
                      {(stats.revenueMonth / stats.activeBusinesses).toFixed(2)}{' '}
                      per client
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100">
                    <Activity className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      System Health
                    </h3>
                    <p className="text-xl font-bold text-purple-600">
                      100% Uptime
                    </p>
                    <p className="text-sm text-gray-500">API Response: 145ms</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Distribution */}
            <div className="bg-white p-6 rounded-lg shadow border mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Plan Distribution
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Businesses
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % of Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Monthly Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {planData.map((plan) => (
                      <tr key={plan.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Shield
                              className={`h-5 w-5 ${
                                plan.id === 'starter'
                                  ? 'text-gray-400'
                                  : plan.id === 'professional'
                                  ? 'text-blue-500'
                                  : plan.id === 'business'
                                  ? 'text-purple-500'
                                  : 'text-indigo-500'
                              } mr-2`}
                            />
                            <span className="font-medium">{plan.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {plan.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1 max-w-[150px]">
                            <div
                              className={`h-2.5 rounded-full ${
                                plan.id === 'starter'
                                  ? 'bg-gray-400'
                                  : plan.id === 'professional'
                                  ? 'bg-blue-500'
                                  : plan.id === 'business'
                                  ? 'bg-purple-500'
                                  : 'bg-indigo-500'
                              }`}
                              style={{ width: `${plan.percentTotal}%` }}
                            ></div>
                          </div>
                          <span className="text-xs">{plan.percentTotal}%</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium">
                            ${plan.revenue.toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-500 ml-1">
                            (${plan.price}/mo)
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Actions and Recent Activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow border">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart className="h-5 w-5 mr-2 text-indigo-500" />
                  Platform Metrics
                </h2>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        User Acquisition
                      </span>
                      <span className="text-sm text-green-600">
                        +12% this month
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-green-500 h-2.5 rounded-full"
                        style={{ width: '70%' }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        Retention Rate
                      </span>
                      <span className="text-sm text-green-600">92%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-500 h-2.5 rounded-full"
                        style={{ width: '92%' }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        Conversion Rate
                      </span>
                      <span className="text-sm text-yellow-600">38%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-yellow-500 h-2.5 rounded-full"
                        style={{ width: '38%' }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium">
                        Support Response
                      </span>
                      <span className="text-sm text-green-600">3.2 hours</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-indigo-500 h-2.5 rounded-full"
                        style={{ width: '85%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow border">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Recent Activity
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        New business signup
                      </p>
                      <p className="text-sm text-gray-500">
                        Thompson Consulting joined on Professional plan
                      </p>
                      <p className="text-xs text-gray-400 mt-1">2 hours ago</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Plan upgrade
                      </p>
                      <p className="text-sm text-gray-500">
                        Wilson Accounting upgraded from Starter to Business
                      </p>
                      <p className="text-xs text-gray-400 mt-1">5 hours ago</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Enterprise package purchased
                      </p>
                      <p className="text-sm text-gray-500">
                        Garcia & Associates purchased code package
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Yesterday at 2:45 PM
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        System maintenance completed
                      </p>
                      <p className="text-sm text-gray-500">
                        Database optimization and security updates deployed
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Yesterday at 11:30 AM
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
